<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useAccount } from 'dashboard/composables/useAccount';

const store = useStore();
const { accountId } = useAccount();

const isLoading = ref(false);
const fromDate = ref('');
const toDate = ref('');
const showFilters = ref(false);

const analytics = computed(() => store.getters['nexceoAiLogs/getAnalytics']);

async function load() {
  isLoading.value = true;
  try {
    const filters = {};
    if (fromDate.value) filters.from = fromDate.value;
    if (toDate.value) filters.to = toDate.value;
    await store.dispatch('nexceoAiLogs/fetchAnalytics', {
      accountId: accountId.value,
      filters,
    });
  } finally {
    isLoading.value = false;
  }
}

function clearFilters() {
  fromDate.value = '';
  toDate.value = '';
  load();
}

const hasFilters = computed(() => fromDate.value || toDate.value);

onMounted(load);

function fmtCost(v) {
  if (!v || Number(v) === 0) return '$0.0000';
  return `$${Number(v).toFixed(4)}`;
}

function fmtMs(v) {
  if (!v) return '—';
  if (v >= 1000) return `${(v / 1000).toFixed(1)}s`;
  return `${v}ms`;
}

const STAT_CARDS = computed(() => {
  const m = analytics.value;
  if (!m) return [];
  return [
    {
      label: 'AI Calls',
      value: m.total_logs ?? 0,
      icon: 'i-lucide-activity',
      color: '#6366f1',
    },
    {
      label: 'Failed Calls',
      value: m.failed_calls ?? 0,
      icon: 'i-lucide-alert-circle',
      color: '#ef4444',
      danger: true,
    },
    {
      label: 'Replies Sent',
      value: m.customer_replies ?? 0,
      icon: 'i-lucide-send',
      color: '#10b981',
    },
    {
      label: 'Handoffs',
      value: m.handoffs ?? 0,
      icon: 'i-lucide-user-check',
      color: '#f97316',
    },
    {
      label: 'Site Visits (AI)',
      value: m.site_visits_by_ai ?? 0,
      icon: 'i-lucide-calendar-check',
      color: '#3b82f6',
    },
    {
      label: 'Tool Actions',
      value: m.tool_actions ?? 0,
      icon: 'i-lucide-wrench',
      color: '#8b5cf6',
    },
    {
      label: 'Avg Response',
      value: fmtMs(m.avg_response_ms),
      icon: 'i-lucide-timer',
      color: '#f59e0b',
    },
    {
      label: 'Total Tokens',
      value: (m.total_tokens || 0).toLocaleString(),
      icon: 'i-lucide-hash',
      color: '#64748b',
    },
    {
      label: 'Est. Cost',
      value: fmtCost(m.estimated_cost_usd),
      icon: 'i-lucide-dollar-sign',
      color: '#0ea5e9',
    },
  ];
});

const providerEntries = computed(() => {
  const pb = analytics.value?.provider_breakdown;
  if (!pb) return [];
  return Object.entries(pb);
});

const PROVIDER_COLORS = {
  openai: '#10b981',
  anthropic: '#f97316',
  custom: '#8b5cf6',
};

function providerColor(name) {
  return PROVIDER_COLORS[name?.toLowerCase()] || '#64748b';
}
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-hidden bg-n-background">
    <!-- ── Header ── -->
    <div
      class="flex items-center gap-3 px-4 h-14 bg-n-background border-b border-n-weak shrink-0"
    >
      <!-- Title -->
      <span class="i-lucide-bar-chart-3 size-4 text-woot-500 shrink-0" />
      <h1 class="text-base font-semibold text-n-slate-12 truncate">
        AI Analytics
      </h1>

      <div class="flex-1" />

      <!-- Filter toggle (mobile) -->
      <button
        class="relative flex items-center justify-center w-8 h-8 rounded-lg border transition-colors sm:hidden"
        :class="
          showFilters
            ? 'bg-woot-500 border-woot-500 text-white'
            : 'border-n-weak bg-n-background text-n-slate-8 hover:border-woot-400 hover:text-woot-500'
        "
        @click="showFilters = !showFilters"
      >
        <span class="i-lucide-calendar-range size-3.5" />
        <span
          v-if="hasFilters"
          class="absolute -top-1.5 -right-1.5 w-2 h-2 rounded-full bg-woot-500"
        />
      </button>

      <!-- Date filters (desktop) -->
      <div class="hidden sm:flex items-center gap-2">
        <div class="relative">
          <span
            class="absolute left-2.5 top-1/2 -translate-y-1/2 i-lucide-calendar size-3.5 text-n-slate-7 pointer-events-none"
          />
          <input
            v-model="fromDate"
            type="date"
            class="pl-8 pr-3 py-1.5 text-sm border border-n-weak rounded-lg bg-n-background text-n-slate-11 outline-none focus:border-woot-500 transition-colors"
          />
        </div>
        <span class="text-xs text-n-slate-7">to</span>
        <div class="relative">
          <span
            class="absolute left-2.5 top-1/2 -translate-y-1/2 i-lucide-calendar size-3.5 text-n-slate-7 pointer-events-none"
          />
          <input
            v-model="toDate"
            type="date"
            class="pl-8 pr-3 py-1.5 text-sm border border-n-weak rounded-lg bg-n-background text-n-slate-11 outline-none focus:border-woot-500 transition-colors"
          />
        </div>
        <button
          :disabled="isLoading"
          class="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-woot-500 text-white hover:bg-woot-600 disabled:opacity-50 transition-colors"
          @click="load"
        >
          <span
            class="size-3.5"
            :class="
              isLoading ? 'i-lucide-loader-2 animate-spin' : 'i-lucide-search'
            "
          />
          Apply
        </button>
        <button
          v-if="hasFilters"
          class="flex items-center justify-center w-8 h-8 rounded-lg border border-n-weak text-n-slate-8 hover:border-ruby-400 hover:text-ruby-500 transition-colors"
          title="Clear filters"
          @click="clearFilters"
        >
          <span class="i-lucide-x size-3.5" />
        </button>
      </div>

      <!-- Reload -->
      <button
        :disabled="isLoading"
        class="flex items-center justify-center w-8 h-8 rounded-lg border border-n-weak text-n-slate-8 hover:border-woot-400 hover:text-woot-500 disabled:opacity-50 transition-colors"
        title="Refresh"
        @click="load"
      >
        <span
          class="size-3.5"
          :class="
            isLoading ? 'i-lucide-loader-2 animate-spin' : 'i-lucide-refresh-cw'
          "
        />
      </button>
    </div>

    <!-- ── Mobile filter bar ── -->
    <div
      v-if="showFilters"
      class="sm:hidden flex items-center gap-2 px-4 py-2.5 border-b border-n-weak bg-n-solid-1 shrink-0 flex-wrap"
    >
      <input
        v-model="fromDate"
        type="date"
        class="flex-1 min-w-0 px-3 py-1.5 text-sm border border-n-weak rounded-lg bg-n-background text-n-slate-11 outline-none focus:border-woot-500"
      />
      <span class="text-xs text-n-slate-7 shrink-0">to</span>
      <input
        v-model="toDate"
        type="date"
        class="flex-1 min-w-0 px-3 py-1.5 text-sm border border-n-weak rounded-lg bg-n-background text-n-slate-11 outline-none focus:border-woot-500"
      />
      <button
        :disabled="isLoading"
        class="px-3 py-1.5 text-sm rounded-lg bg-woot-500 text-white hover:bg-woot-600 disabled:opacity-50 transition-colors shrink-0"
        @click="load"
      >
        Apply
      </button>
      <button
        v-if="hasFilters"
        class="px-3 py-1.5 text-sm rounded-lg border border-n-weak text-n-slate-8 hover:text-ruby-500 transition-colors shrink-0"
        @click="clearFilters"
      >
        Clear
      </button>
    </div>

    <!-- ── Loading (initial) ── -->
    <div
      v-if="isLoading && !analytics"
      class="flex-1 flex items-center justify-center"
    >
      <div class="text-center">
        <span
          class="i-lucide-loader-2 size-8 animate-spin text-woot-500 mx-auto block mb-3"
        />
        <p class="text-sm text-n-slate-8">Loading analytics…</p>
      </div>
    </div>

    <!-- ── Empty state ── -->
    <div v-else-if="!analytics" class="flex-1 flex items-center justify-center">
      <div class="text-center max-w-xs">
        <div
          class="mx-auto mb-4 flex items-center justify-center w-14 h-14 rounded-2xl bg-woot-50 dark:bg-woot-500/10"
        >
          <span class="i-lucide-bar-chart-3 size-7 text-woot-500" />
        </div>
        <p class="text-sm font-medium text-n-slate-11 mb-1">
          No analytics data yet
        </p>
        <p class="text-xs text-n-slate-7">
          AI activity data will appear here once the AI starts processing leads.
        </p>
      </div>
    </div>

    <!-- ── Content ── -->
    <div v-else class="flex-1 overflow-auto p-5 space-y-5">
      <!-- Stale overlay while refreshing -->
      <div
        v-if="isLoading"
        class="flex items-center gap-2 px-3 py-2 rounded-lg bg-woot-50 dark:bg-woot-500/10 border border-woot-200 dark:border-woot-500/20 text-xs text-woot-600 dark:text-woot-400"
      >
        <span class="i-lucide-loader-2 size-3.5 animate-spin" />
        Refreshing data…
      </div>

      <!-- Stat cards grid -->
      <div class="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <div
          v-for="card in STAT_CARDS"
          :key="card.label"
          class="flex items-center gap-3 p-4 rounded-xl border border-n-weak bg-n-solid-1 hover:border-n-strong transition-colors"
        >
          <!-- Icon bubble -->
          <div
            class="flex items-center justify-center w-9 h-9 rounded-xl shrink-0"
            :style="{ backgroundColor: card.color + '18' }"
          >
            <span
              class="size-4"
              :class="card.icon"
              :style="{ color: card.color }"
            />
          </div>
          <!-- Text -->
          <div class="min-w-0">
            <p class="text-[11px] text-n-slate-7 leading-tight truncate">
              {{ card.label }}
            </p>
            <p
              class="text-xl font-bold leading-tight mt-0.5 tabular-nums truncate"
              :style="
                card.danger && Number(card.value) > 0
                  ? { color: '#ef4444' }
                  : { color: 'inherit' }
              "
              :class="
                card.danger && Number(card.value) > 0 ? '' : 'text-n-slate-12'
              "
            >
              {{ card.value ?? '—' }}
            </p>
          </div>
        </div>
      </div>

      <!-- Provider breakdown -->
      <div
        v-if="providerEntries.length"
        class="rounded-xl border border-n-weak bg-n-solid-1 p-4"
      >
        <div class="flex items-center gap-2 mb-3">
          <span class="i-lucide-cpu size-3.5 text-n-slate-8" />
          <h2
            class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider"
          >
            Provider Breakdown
          </h2>
        </div>
        <div class="flex flex-wrap gap-2">
          <div
            v-for="[provider, count] in providerEntries"
            :key="provider"
            class="flex items-center gap-2 px-3 py-2 rounded-lg border border-n-weak bg-n-background text-sm"
          >
            <span
              class="w-2 h-2 rounded-full shrink-0"
              :style="{ backgroundColor: providerColor(provider) }"
            />
            <span class="font-medium text-n-slate-11 capitalize">
              {{ provider }}
            </span>
            <span class="text-n-slate-7">{{ count }} calls</span>
          </div>
        </div>
      </div>

      <!-- Date range note (when filtered) -->
      <p v-if="hasFilters" class="text-xs text-n-slate-7 text-right">
        Showing data from
        <span class="font-medium text-n-slate-9">{{ fromDate || '—' }}</span>
        to
        <span class="font-medium text-n-slate-9">{{ toDate || '—' }}</span>
      </p>
    </div>
  </div>
</template>
